<template>
  <b-container>
    <tool-header
      tool-name="Client Config Asset Manager"
      tool-icon-text="(Mr. Manager)" />
    <b-tabs
      v-model="tabIndex"
      active-nav-item-class="font-weight-bold bg-white text-dark"
      class="tabHeader float-left mt-2"
      card
      justified
      @activate-tab="onTabChanged">
      <b-tab
        v-for="(env, index) in environments"
        :key="env.name"
        :title="env.name">
        <b-row
          align-v="center"
          class="my-2">
          <b-col cols="5">
            <b-input
              v-model="filterText"
              type="text"
              placeholder="Search in this folder..." />
          </b-col>
          <b-col cols="7">
            <div class="float-right">
              <b-btn-toolbar>
                <b-btn-group
                  size="sm"
                  class="mx-1">
                  <b-btn
                    v-b-tooltip.hover
                    :disabled="!env.selectedAssets || !env.selectedAssets.length"
                    title="Download Selected"
                    variant="search"
                    @click.stop="onDownloadMultipleAssets">
                    <i class="download-icon" /> Download
                  </b-btn>
                  <b-btn
                    v-if="canAssetManagerEdit"
                    v-b-tooltip.hover
                    :disabled="!env.selectedAssets || !env.selectedAssets.length"
                    title="Delete Selected"
                    variant="search"
                    @click.stop="onDeleteSelectedAssets">
                    <i class="delete-icon" /> Delete
                  </b-btn>
                </b-btn-group>
                <b-btn-group
                  v-if="canAssetManagerEdit"
                  size="sm">
                  <b-btn
                    v-b-tooltip.hover
                    title="Create a folder in the current path"
                    variant="search"
                    size="sm"
                    @click.stop="$refs.createFolderModal.show()">
                    <i class="fas fa-folder-plus" /> Create Folder
                  </b-btn>
                  <b-btn
                    v-b-tooltip.hover
                    title="Choose a file or files to upload to the current path"
                    variant="primary"
                    size="sm"
                    @click.stop="$refs.assetUploadModal.show()">
                    <i class="fas fa-cloud-upload-alt" /> Upload to Folder
                  </b-btn>
                </b-btn-group>
              </b-btn-toolbar>
            </div>
          </b-col>
        </b-row>
        <b-row align-v="center">
          <b-col cols="8">
            <div class="mt-2 mb-3 breadcrumbs">
              <b-link
                class="mx-2"
                @click.stop="$router.push({query: { path: `${index}:`}})">
                <i class="fas fa-home" />
              </b-link>
              <span
                v-for="element in env.pathItems"
                :key="element.href">
                <span class="mx-2">/</span>
                <b-link
                  v-if="!element.active"
                  @click.stop="$router.push({query: { path: `${index}:${element.href}`}})">
                  {{ element.text }}
                </b-link>
                <span v-else>
                  {{ element.text }}
                </span>
              </span>
            </div>
          </b-col>
          <b-col cols="4">
            <b-form-select
              v-model="jumpToFolder"
              :options="jumpToFolderOptions"
              class="mb-2 float-right"
              @change="onJumpToFolder" />
          </b-col>
        </b-row>
        <files-table
          :ref="'filesTable' + index"
          :index="index"
          :files="env.files"
          :filter="filterText"
          :path="env.path"
          :loading="env.loading"
          :editable="canAssetManagerEdit"
          @update-path="(idx, path) => $router.push({ query: { path: `${idx}:${path}` }})"
          @preview="onPreviewAsset"
          @download="onDownloadAsset"
          @delete="onDeleteAsset"
          @copy-url="copyAssetUrl"
          @asset-selected="onAssetSelected" />
      </b-tab>
    </b-tabs>
    <b-modal
      id="asset-upload"
      ref="assetUploadModal"
      hide-header
      hide-footer
      size="lg">
      <asset-upload
        :post-action="environments[tabIndex].uploadPath + '/' + environments[tabIndex].path"
        :allow-multiple-uploads="true"
        :existing-files="environments[tabIndex].files.map(file => file.name)"
        @asset-url-selected="onAssetSelect"
        @cancel="$refs.assetUploadModal.hide()">
        <template v-slot:additional-info>
          <b-button
            v-b-toggle.collapse-guidelines
            variant="link"
            class="px-0">
            View Guidelines
          </b-button>
          <b-collapse
            id="collapse-guidelines"
            class="mt-2">
            <b-card>
              <div class="card-text">
                <b-table-simple
                  small
                  borderless
                  striped>
                  <b-thead>
                    <b-th>Description</b-th>
                    <b-th>Image Size (px)</b-th>
                  </b-thead>
                  <b-tbody>
                    <b-tr>
                      <b-td>More Menu “Sections” section (displays on iOS only)</b-td>
                      <b-td>90x90</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td>More Menu Apps & Games section</b-td>
                      <b-td>180x180</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td>Team Page Insider Section</b-td>
                      <b-td>1280x720</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td>Team Page More From Section (displays on iOS only)</b-td>
                      <b-td>90x90</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td>Banners (Phone and Tablet TV EPG)</b-td>
                      <b-td>960x150</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td>Banners (Tablet Portrait)</b-td>
                      <b-td>1456x180</b-td>
                    </b-tr>
                    <b-tr>
                      <b-td>Banners (Tablet Landscape)</b-td>
                      <b-td>1940x180</b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </div>
            </b-card>
          </b-collapse>
        </template>
      </asset-upload>
    </b-modal>
    <b-modal
      ref="assetPreviewModal"
      title="Preview"
      size="lg"
      ok-only
      ok-variant="secondary">
      <b-container
        v-if="assetToPreview"
        class="mb-2">
        <div>
          <strong>File Name</strong>: {{ assetToPreview.name }}
        </div>
        <div>
          <strong>Size</strong>: {{ assetToPreview.bytes | formatSize }}
        </div>
        <div>
          <strong>Last Modified</strong>: {{ assetToPreview.lastModified | formatDateTime }}
        </div>
      </b-container>
      <b-img
        v-if="assetToPreview"
        :src="assetToPreview.url"
        fluid
        style="display: block; margin: auto" />
    </b-modal>
    <b-modal
      ref="createFolderModal"
      title="Create Folder"
      @cancel="$refs.createFolderModal.hide()"
      @ok="onCreateFolder"
      @show="createFolderName = null">
      <form
        ref="form"
        @submit.stop.prevent="onCreateFolder">
        <b-form-group
          label="Name"
          label-for="folder-input"
          invalid-feedback="Please enter a folder name">
          <b-form-input
            id="name-input"
            v-model="createFolderName"
            autofocus
            required />
        </b-form-group>
      </form>
    </b-modal>
  </b-container>
</template>

<script>
  import {mapGetters} from 'vuex'
  import AssetUpload from '@/components/AssetUpload'
  import FilesTable from '@/components/FilesTable'
  import ToolHeader from '@/components/ToolHeader'
  import {ClientContent} from '@/services/api'
  import common from '@/services/common'

  // The pattern of the 'path' query param that allows for navigating amongst tabs/paths using the browser forward/back buttons.
  // The format is {tabIndex}:/path/in/folder
  const PATH_PATTERN = new RegExp("(\\d):(.*)")

  export default {
    name: 'AssetManager',
    components: {
      AssetUpload,
      FilesTable,
      ToolHeader,
    },
    data() {
      return {
        environments: [],
        tabIndex: 0,
        filterText: '',
        assetToPreview: null,
        createFolderName: null,
        jumpToFolder: null,
        jumpToFolderOptions: [
          {value: null, text: 'Jump to folder...'},
          {value: 'ios/gameday/backdrops', text: 'iOS Gameday Backdrops'},
          {value: 'ios/postseason/DualTeamLogos', text: 'iOS Post-Season Dual-Team-Logos'},
        ],
      }
    },
    computed: {
      ...mapGetters([
        'betaEnvName',
        'liveEnvName',
        'isAdmin',
        'canAssetManagerEdit',
        'canAssetManagerPublish',
      ]),
      loading() {
        return this.environments.some(t => t.loading)
      },
    },
    watch: {
      "$route.query.path"(newVal) {
        const parsedPath = PATH_PATTERN.exec(newVal)
        if (parsedPath) {
          const desiredTab = parseInt(parsedPath[1])
          this.updatePath(desiredTab, parsedPath[2])
        }
      },
    },
    created() {
      this.environments = [
        {
          name: this.betaEnvName,
          path: '',
          pathItems: [],
          files: [],
          loading: true,
          selectedAssets: [],
          getFilesFn: ClientContent.getBetaFiles,
          deleteFileFn: ClientContent.deleteBetaFile,
          createFolderFn: ClientContent.createBetaFolder,
          deleteFolderFn: ClientContent.deleteBetaFolder,
          uploadPath: '/api/client-content/v1/beta/files',
        },
        {
          name: this.liveEnvName,
          path: '',
          pathItems: [],
          files: [],
          loading: true,
          selectedAssets: [],
          getFilesFn: ClientContent.getFiles,
          deleteFileFn: ClientContent.deleteFile,
          createFolderFn: ClientContent.createFolder,
          deleteFolderFn: ClientContent.deleteFolder,
          uploadPath: '/api/client-content/v1/files',
        },
      ]

      this.refreshAllAssets()
      this.$router.replace({ query: { path: "0:" }})
    },
    methods: {
      refreshAllAssets() {
        this.refreshAssets(this.environments[0])
        this.refreshAssets(this.environments[1])
        console.log(`Envs after load: [${JSON.stringify(this.environments)}]`)
      },
      async refreshAssets(env) {
        env.loading = true
        try {
          env.files = await env.getFilesFn(env.path)
          env.pathItems = this.getPathItems(env.path)
          env.selectedAssets = []
          env.loading = false
        } catch (error) {
          env.loading = false
          console.error(`Unable to retrieve ${env.name} files`, error)
          throw error
        }
      },
      getPathItems(path) {
        if (!path || path === '/') {
          return []
        }

        // Build the items to be used in the breadcrumb path
        let href = ''
        const items = path.split('/').map((element) => {
          href += href.length > 0 ? `/${element}` : `${element}`
          return {
            text: element,
            href: href,
          }
        })

        // Mark the last element in the path as active
        if (items && items.length > 0) {
          items[items.length - 1].active = true
        }

        return items
      },
      updatePath(index, path) {
        console.log(`Updating tab ${index} path to ${path}`)

        if (this.environments && this.environments.length >= index) {
          const env = this.environments[index]

          env.loading = true
          env.path = path
          env.pathItems = this.getPathItems(path)
          env.selectedAssets = []
          env.files = []

          this.refreshAssets(env)
          this.tabIndex = index
        }
      },
      onAssetSelect(url) {
        this.$refs.assetUploadModal.hide()

        if (url) {
          this.refreshAllAssets()
        } else {
          console.warn('Ignoring invalid asset selection')
        }
      },
      async onDeleteSelectedAssets() {
        return this.showDeleteModal(this.environments[this.tabIndex].selectedAssets)
      },
      async onDeleteAsset(asset) {
        return this.showDeleteModal([asset])
      },
      async showDeleteModal(assets) {
        let msg = `Are you sure you wish to delete the following asset${assets.length > 1 ? 's' : ''}?\n`
        assets.forEach(item => {
          msg += `\n- ${item.name}`
        })

        const modalConfirmed = await this.$confirm.open(msg)

        if (modalConfirmed) {
          const env = this.environments[this.tabIndex]
          let deletedFiles = 0
          let deletedFolders = 0

          await Promise.all(assets.map(async asset => {
            if (asset.contentType === 'folder') {
              await env.deleteFolderFn(`${env.path}/${asset.name}`)
              deletedFolders++
            } else {
              await env.deleteFileFn(`${env.path}/${asset.name}`)
              deletedFiles++
            }
          }))

          if (deletedFiles > 0 || deletedFolders > 0) {
            let msg = 'Successfully deleted '

            if (deletedFolders > 0) {
              msg += `${deletedFolders} folder` + (deletedFolders > 1 ? 's' : '')
            }
            if (deletedFiles > 0 && deletedFolders > 0) {
              msg += 'and '
            }
            if (deletedFiles > 0) {
              msg += `${deletedFiles} file` + (deletedFiles > 1 ? 's' : '')
            }
            this.$alert.success(msg)
          } else {
            this.$alert.warning(`No assets were deleted`)
          }
          await this.refreshAssets(env)
        }
      },
      onDownloadMultipleAssets() {
        // Only files are supported for download at the moment so folders are filtered out
        this.environments[this.tabIndex].selectedAssets
          .filter(asset => asset.contentType !== 'folder')
          .forEach(item => {
            common.downloadFile(item.url, item.name)
          })
      },
      onDownloadAsset(assetUrl, asset) {
        return common.downloadFile(`/api/client-content/proxy/v1?target=${assetUrl}`, asset)
      },
      async onCreateFolder() {
        this.$refs.createFolderModal.hide()

        if (this.createFolderName) {
          // Create a folder in the bucket for the environment associated with the selected tab and path with the provided name
          try {
            await this.environments[this.tabIndex].createFolderFn(`${this.environments[this.tabIndex].path}/${this.createFolderName}`)
            this.$alert.success(`Successfully created folder ${this.createFolderName}`)
            await this.refreshAssets(this.environments[this.tabIndex])
          } catch (error) {
            this.$alert.error(`Error creating folder ${this.createFolderName}`)
            throw error
          }
        }
      },
      onPreviewAsset(asset) {
        this.assetToPreview = asset
        console.log(`Previewing asset ${JSON.stringify(asset)}`)
        this.$refs.assetPreviewModal.show()
      },
      async copyAssetUrl(url) {
        await this.$copyText(url)
        this.$alert.success('Link copied!')
      },
      onAssetSelected(index, assets) {
        this.environments[index].selectedAssets = assets
      },
      onJumpToFolder() {
        if (this.jumpToFolder !== null) {
          if (this.jumpToFolder !== this.environments[this.tabIndex].path) {
            this.$router.push({query: { path: `${this.tabIndex}:${this.jumpToFolder}` }})
          }
          this.jumpToFolder = null
        }
      },
      onTabChanged(newTabIdx, oldTabIdx, evt) {
        const parsedPath = PATH_PATTERN.exec(this.$route.query.path)
        if (parsedPath) {
          const pathTab = parseInt(parsedPath[1])
          if (pathTab !== newTabIdx) {
            // This happens if the user is switching tabs by clicking to another tab rather than via query params.
            // In this case, prevent the default tab switching and switch tabs by updating the path query param instead.
            evt.preventDefault()
            this.$router.push({ query: { path: `${newTabIdx}:` }})
          }
        }
      },
    },
  }
</script>

<style scoped>
  .tabHeader {
    width: 100%;
  }

  .download-icon {
    background-image: url(../../public/img/icons/download-icon.svg);
    background-size: cover;
    display: inline-block;
    height: 15px;
    width: 15px;
    vertical-align: text-bottom;
  }

  .delete-icon {
    background-image: url(../../public/img/icons/trash-icon.svg);
    background-size: cover;
    display: inline-block;
    height: 15px;
    width: 15px;
    vertical-align: text-bottom;
  }

  .breadcrumbs {
    font-size: 1.5rem;
  }
</style>
